import { useEffect, useState } from "react";
import { normalizeSrc } from "@/lib/images";
import { Link } from "react-router-dom";
import { useLang } from "@/context/LanguageContext";
import { PageHero } from "@/components/PageHero";
import Seo from "@/components/Seo";
import { HERO_ART } from "@/lib/heroes";
import InviteButton from "@/components/InviteButton";
import { fetchEvents } from "@/lib/api";
import { EVENT_TYPES, eventTypeMeta, formatEventDate, eventDayParts } from "@/lib/events";
import { MapPin, Globe, Loader2, CalendarDays, ArrowRight } from "lucide-react";

export default function Events() {
  const { pick } = useLang();
  const en = pick("", "x") === "x";
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [type, setType] = useState("");
  const [host, setHost] = useState("");
  const [when, setWhen] = useState("upcoming");

  useEffect(() => {
    setLoading(true);
    fetchEvents({ when, ...(type ? { type } : {}), ...(host ? { host } : {}) })
      .then(setEvents).finally(() => setLoading(false));
  }, [type, host, when]);

  const chip = (active) => `border px-3 py-1.5 text-xs transition-colors ${active ? "border-saffron bg-saffron text-white" : "border-line text-ink-2 hover:border-saffron hover:text-saffron"}`;

  return (
    <div data-testid="events-page">
      <Seo path="/events" title="Events — Kathas, Yagyas, Festivals & Talks on Dharmartha"
        description="Discover upcoming kathas, yagyas, festival celebrations and spiritual talks — hosted by Dharmartha and the wider community, in person and online." />
      <PageHero art={HERO_ART.knowledge} overline={pick("समुदाय · आयोजन", "Community · Events")}
        deva="आयोजन एवं उत्सव" en={pick("आयोजन एवं उत्सव", "Events & Gatherings")}
        tagline={pick("कथा, यज्ञ, उत्सव एवं प्रवचन — धर्मार्थ एवं समुदाय द्वारा, प्रत्यक्ष एवं ऑनलाइन।", "Kathas, yagyas, festival celebrations and talks — by Dharmartha and the community, in person and online.")} />

      <section className="mx-auto max-w-5xl px-5 py-12 md:px-8">
        {/* Filters */}
        <div className="mb-6 flex justify-end"><InviteButton context={pick("आयोजन", "Events")} testid="events-invite" /></div>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex border border-line">
            {[["upcoming", pick("आगामी", "Upcoming")], ["past", pick("विगत", "Past")]].map(([k, l]) => (
              <button key={k} onClick={() => setWhen(k)} data-testid={`events-when-${k}`}
                className={`px-3 py-1.5 text-xs transition-colors ${when === k ? "bg-saffron text-white" : "text-ink-2 hover:text-ink"}`}>{l}</button>
            ))}
          </div>
          <span className="mx-1 h-4 w-px bg-line" />
          <button onClick={() => setType("")} data-testid="events-type-all" className={chip(!type)}>{pick("सभी", "All")}</button>
          {EVENT_TYPES.map((t) => (
            <button key={t.key} onClick={() => setType(t.key)} data-testid={`events-type-${t.key}`} className={chip(type === t.key)}>{pick(t.deva, t.en)}</button>
          ))}
          <span className="mx-1 h-4 w-px bg-line" />
          <button onClick={() => setHost(host === "dharmartha" ? "" : "dharmartha")} data-testid="events-host-dharmartha" className={chip(host === "dharmartha")}>{pick("धर्मार्थ द्वारा", "By Dharmartha")}</button>
          <button onClick={() => setHost(host === "community" ? "" : "community")} data-testid="events-host-community" className={chip(host === "community")}>{pick("समुदाय द्वारा", "By community")}</button>
        </div>

        {/* Grid */}
        <div className="mt-8 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {loading ? (
            <div className="col-span-full flex items-center gap-2 py-10 text-ink-3"><Loader2 className="h-4 w-4 animate-spin" /> {pick("लोड हो रहा…", "Loading…")}</div>
          ) : events.length === 0 ? (
            <div className="col-span-full py-14 text-center text-ink-3" data-testid="events-empty">
              {when === "upcoming" ? pick("कोई आगामी आयोजन नहीं।", "No upcoming events right now.") : pick("कोई विगत आयोजन नहीं।", "No past events.")}
            </div>
          ) : events.map((e) => {
            const tm = eventTypeMeta(e.type);
            const dp = eventDayParts(e.start);
            return (
              <Link key={e.id} to={`/events/${e.id}`} data-testid={`event-card-${e.id}`} className="group flex flex-col overflow-hidden border border-line bg-surface transition-shadow hover:shadow-md">
                <div className="relative h-32 w-full overflow-hidden" style={{ backgroundColor: `${tm.accent}14` }}>
                  {e.image_url ? <img src={normalizeSrc(e.image_url)} alt="" className="h-full w-full object-cover" /> : <div className="flex h-full items-center justify-center"><CalendarDays className="h-10 w-10" style={{ color: tm.accent }} /></div>}
                  <span className="absolute left-2 top-2 rounded-sm px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider text-white" style={{ backgroundColor: tm.accent }}>{pick(tm.deva, tm.en)}</span>
                  <span className="absolute right-2 top-2 flex flex-col items-center rounded bg-[var(--surface)]/95 px-2 py-1 leading-none text-ink shadow">
                    <span className="font-serif-latin text-lg">{dp.day}</span>
                    <span className="text-[9px] uppercase tracking-wider text-ink-3">{dp.mon}</span>
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-4">
                  <h3 className="font-serif-latin text-lg text-ink transition-colors group-hover:text-saffron">{e.title}</h3>
                  <p className="mt-1 text-xs text-ink-3">{formatEventDate(e.start, en)}</p>
                  {e.organizer_name && <p className="mt-1 text-xs text-gold">{e.host === "dharmartha" ? pick("धर्मार्थ", "Dharmartha") : e.organizer_name}</p>}
                  {e.description && <p className="mt-2 line-clamp-2 text-sm text-ink-2">{e.description}</p>}
                  <div className="mt-3 flex items-center gap-3 text-xs text-ink-3">
                    {e.mode !== "online" && (e.city || e.venue) && <span className="inline-flex items-center gap-1"><MapPin className="h-3.5 w-3.5" /> {e.city || e.venue}</span>}
                    {e.mode !== "in_person" && <span className="inline-flex items-center gap-1"><Globe className="h-3.5 w-3.5" /> {pick("ऑनलाइन", "Online")}</span>}
                    <span className="ml-auto inline-flex items-center gap-1 text-saffron opacity-0 transition-opacity group-hover:opacity-100">{pick("देखें", "View")} <ArrowRight className="h-3.5 w-3.5" /></span>
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>
    </div>
  );
}
